import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import {
  useQuery,
  ApolloError,
  useApolloClient,
  useMutation,
} from '@apollo/client';
import { genericToastAlert } from 'utils/toastHelpers';
import { Space } from 'interfaces/space';
import { SECTION_QUERY } from 'schema/Section/queries';
import { arrayMove } from '@dnd-kit/sortable';
import { REORDER_SPACE_MUTATION } from 'schema/Space/mutations';
import { DragEndEvent } from '@dnd-kit/core';
import { getSpaceInfo } from 'schema/Space/helpers';

interface SpacesContextValue {
  spaceList: Space[];
  isLoading: boolean;
  error: ApolloError | undefined;
  onSpaceDragEnd: (event: DragEndEvent) => void;
}

const SpacesContext = createContext<SpacesContextValue | undefined>(undefined);

export const useSpaces = (): SpacesContextValue => {
  const context = useContext(SpacesContext);
  if (!context) {
    throw new Error('useSpaces must be used within a SpacesProvider');
  }
  return context;
};

interface SpacesProviderProps {
  children: ReactNode;
  communityId: string;
}

export const SpacesProvider = ({
  children,
  communityId,
}: SpacesProviderProps) => {
  const client = useApolloClient();
  const { loading: queryLoading, data, error } = useQuery(SECTION_QUERY, {
    variables: { communityId },
    notifyOnNetworkStatusChange: true,
    onError: () => {
      genericToastAlert();
    },
  });
  const [spaceList, setSpaceList] = useState<Space[]>([]);

  useEffect(() => {
    if (data) {
      setSpaceList(getSpaceInfo(client, data, communityId));
    }
  }, [data]);

  const [reorderSpaceMutation, { loading: reorderLoading }] = useMutation(
    REORDER_SPACE_MUTATION,
    {
      onError: genericToastAlert,
    }
  );

  const onSpaceDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = spaceList.findIndex((item) => item.id === active.id);
      const newIndex = spaceList.findIndex((item) => item.id === over.id);
      setSpaceList((items) => {
        return arrayMove(items, oldIndex, newIndex);
      });
      const { data: reorderSpaceData, errors } = await reorderSpaceMutation({
        variables: {
          input: { id: active.id, position: newIndex },
        },
        refetchQueries: [
          {
            query: SECTION_QUERY,
            variables: {
              communityId,
            },
          },
        ],
      });
      if (
        errors?.length ||
        reorderSpaceData?.reorderCommunitySpace?.userErrors
      ) {
        genericToastAlert();
      }
    }
  };

  const isLoading = queryLoading || reorderLoading;

  return (
    <SpacesContext.Provider
      value={{
        spaceList,
        isLoading,
        onSpaceDragEnd,
        error,
      }}
    >
      {children}
    </SpacesContext.Provider>
  );
};
